import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';

const API_BASE = import.meta.env.VITE_API_URL || '';

/**
 * PM Rework Widget - v2.1
 *
 * Compact operational widget for PM Command Center.
 * Shows: Open count, Scrap alerts, Aging items.
 * No charts or trends - just operational awareness.
 *
 * Props:
 * - onViewEvent: (eventId) => void - View event details
 * - className?: string - Additional CSS classes
 */
export default function PMReworkWidget({ onViewEvent, className = '' }) {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [aging, setAging] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // Fetch stats and events in parallel
        const [statsRes, eventsRes] = await Promise.all([
          fetch(`${API_BASE}/api/rework-events/stats`),
          fetch(`${API_BASE}/api/rework-events?status=open&limit=20`),
        ]);

        if (!statsRes.ok || !eventsRes.ok) {
          throw new Error('Failed to fetch rework data');
        }

        const statsData = await statsRes.json();
        const eventsData = await eventsRes.json();

        setStats(statsData.summary);

        // Filter for scrap alerts and aging items (>4 hours)
        const events = eventsData.events || [];
        setAlerts(events.filter(e => e.severity === 'scrap'));
        setAging(events.filter(e => parseFloat(e.ageHours || 0) > 4 && e.severity !== 'scrap'));
      } catch (err) {
        console.error('[PMReworkWidget] Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchData();

    // Refresh every 2 minutes
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !stats) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Quality Issues</h3>
        <div className="text-center py-4 text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Quality Issues</h3>
        <div className="text-center py-4 text-red-500">{error}</div>
      </div>
    );
  }

  const openCount = parseInt(stats?.open_events || 0);
  const scrapCount = parseInt(stats?.scrap_events || 0);
  const reworkHours = (parseInt(stats?.total_events || 0) * 0.5).toFixed(1); // Rough estimate

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Quality Issues</h3>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-4 p-4 border-b border-gray-100 bg-gray-50">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">{openCount}</div>
          <div className="text-xs text-gray-500 uppercase">Open</div>
        </div>
        <div className="text-center">
          <div className={`text-2xl font-bold ${scrapCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {scrapCount}
          </div>
          <div className="text-xs text-gray-500 uppercase">Scrap</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">{reworkHours}</div>
          <div className="text-xs text-gray-500 uppercase">Hrs (week)</div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 max-h-64 overflow-y-auto">
        {/* Scrap Alerts */}
        {alerts.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-red-500">🚨</span>
              <span className="text-sm font-semibold text-red-700 uppercase">Scrap Alert</span>
            </div>
            {alerts.map((alert) => (
              <AlertItem key={alert.id} event={alert} onView={onViewEvent} variant="scrap" />
            ))}
          </div>
        )}

        {/* Aging Items */}
        {aging.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-amber-500">⏰</span>
              <span className="text-sm font-semibold text-amber-700 uppercase">Aging ({'>'}4h)</span>
            </div>
            {aging.slice(0, 3).map((item) => (
              <AlertItem key={item.id} event={item} onView={onViewEvent} variant="aging" />
            ))}
            {aging.length > 3 && (
              <div className="text-sm text-gray-500 mt-1">
                +{aging.length - 3} more
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {alerts.length === 0 && aging.length === 0 && (
          <div className="text-center py-4 text-gray-500 text-sm">
            {openCount > 0 ? 'All issues under control' : 'No open issues'}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Single alert/aging item row.
 */
function AlertItem({ event, onView, variant }) {
  const ageText = event.createdAt
    ? formatDistanceToNow(new Date(event.createdAt), { addSuffix: true })
    : `${parseFloat(event.ageHours || 0).toFixed(1)}h ago`;

  const bgColor = variant === 'scrap' ? 'bg-red-50 hover:bg-red-100' : 'bg-amber-50 hover:bg-amber-100';
  const textColor = variant === 'scrap' ? 'text-red-900' : 'text-amber-900';

  return (
    <button
      onClick={() => onView?.(event.id)}
      className={`w-full text-left p-2 rounded ${bgColor} ${textColor} transition-colors`}
    >
      <div className="text-sm font-medium truncate">
        {event.projectName || event.jobNumber}
      </div>
      <div className="text-xs opacity-75">
        {event.issueLabel || event.issueType} • {ageText}
      </div>
    </button>
  );
}
